import { API_URL } from '@/lib/api'

// Deliberately NOT the axios `API` instance: the storefront API is
// authenticated via the X-Storefront-Token header, not cookies/XSRF.

export class StorefrontApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message)
    this.name = 'StorefrontApiError'
  }
}

type RequestOptions = {
  cartToken?: string
  body?: unknown
}

export type StorefrontClient = ReturnType<typeof createStorefrontClient>

export const createStorefrontClient = (token: string) => {
  const request = async <T>(
    method: string,
    path: string,
    options: RequestOptions = {},
  ): Promise<T> => {
    const headers: Record<string, string> = {
      'X-Storefront-Token': token,
    }

    if (options.cartToken) {
      headers['X-Cart-Token'] = options.cartToken
    }

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(`${API_URL}/api/storefront/v1${path}`, {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    })

    if (!response.ok) {
      let detail: string | undefined
      try {
        const data = (await response.json()) as {
          detail?: string
          message?: string
          title?: string
        }
        detail = data.detail ?? data.message ?? data.title
      } catch {
        // non-JSON error body
      }
      throw new StorefrontApiError(
        detail ?? `Request failed with status ${response.status}`,
        response.status,
      )
    }

    return (await response.json()) as T
  }

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>('GET', path, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('POST', path, { ...options, body }),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('PATCH', path, { ...options, body }),
    del: <T>(path: string, options?: RequestOptions) =>
      request<T>('DELETE', path, options),
  }
}
